import AnalysisLoading from "@/components/culture-lab/AnalysisLoading";
import ChallengeSelection from "@/components/culture-lab/ChallengeSelection";
import LandingSpaceType from "@/components/culture-lab/LandingSpaceType";
import PropertySpecifics from "@/components/culture-lab/PropertySpecifics";
import ResultsOverview from "@/components/culture-lab/ResultsOverview";
import { useState } from "react";

type CultureLabStep =
  | "welcome"
  | "challenges"
  | "details"
  | "analyzing"
  | "results";

interface PropertyDetails {
  propertyName: string;
  location: string;
  size: string;
  footfall: string;
  businessAge: string;
  currentOfferings: string;
}

interface CultureLabState {
  selectedBusiness: string | null;
  selectedChallenges: string[];
  propertyDetails: PropertyDetails | null;
  currentStep: CultureLabStep;
}

export default function CultureLab() {
  const [state, setState] = useState<CultureLabState>({
    selectedBusiness: null,
    selectedChallenges: [],
    propertyDetails: null,
    currentStep: "welcome",
  });

  const handleBusinessSelect = (businessType: string) => {
    setState((prev) => ({
      ...prev,
      selectedBusiness: businessType,
      currentStep: "challenges",
    }));
  };

  const handleChallengesSelect = (challenges: string[]) => {
    setState((prev) => ({
      ...prev,
      selectedChallenges: challenges,
      currentStep: "details",
    }));
  };

  const handleDetailsSubmit = (details: PropertyDetails) => {
    setState((prev) => ({
      ...prev,
      propertyDetails: details,
      currentStep: "analyzing",
    }));
  };

  const handleAnalysisComplete = () => {
    setState((prev) => ({
      ...prev,
      currentStep: "results",
    }));
  };

  const handleBackToWelcome = () => {
    setState((prev) => ({
      ...prev,
      currentStep: "welcome",
    }));
  };

  const handleBackToChallenges = () => {
    setState((prev) => ({
      ...prev,
      currentStep: "challenges",
    }));
  };

  const renderCurrentStep = () => {
    switch (state.currentStep) {
      case "welcome":
        return <LandingSpaceType onBusinessSelect={handleBusinessSelect} />;
      case "challenges":
        return (
          <ChallengeSelection
            businessType={state.selectedBusiness || ""}
            onChallengesSelect={handleChallengesSelect}
            onBack={handleBackToWelcome}
          />
        );
      case "details":
        return (
          <PropertySpecifics
            businessType={state.selectedBusiness || ""}
            challenges={state.selectedChallenges}
            onDetailsSubmit={handleDetailsSubmit}
            onBack={handleBackToChallenges}
          />
        );
      case "analyzing":
        return (
          <AnalysisLoading
            propertyName={
              state.propertyDetails?.propertyName || "Your Property"
            }
            onAnalysisComplete={handleAnalysisComplete}
          />
        );
      case "results":
        // Check we have all required data before rendering results
        if (!state.selectedBusiness || !state.propertyDetails) {
          return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Error: Missing Analysis Data
                </h2>
                <p className="text-gray-600">
                  Please complete the analysis flow first.
                </p>
              </div>
            </div>
          );
        }

        return (
          <ResultsOverview
            selectedBusiness={state.selectedBusiness}
            selectedChallenges={state.selectedChallenges}
            propertyDetails={state.propertyDetails}
          />
        );
      default:
        return <div>Step not implemented yet</div>;
    }
  };

  return <>{renderCurrentStep()}</>;
}
